import AsyncButton from "@client/controls/Button/AsyncButton/component";
import Form, { type FormFieldInfo, type FormHandles } from "@client/controls/Form/component";
import { type ModalProps } from "@client/controls/Modal/component";
import Modal from "@client/controls/Modal/container";
import { type UploadEndpointInfo } from "@common/components/Media/Upload/types";
import { type DynamicForms } from "@common/controls/Form/DynamicForm/types";
import {
  FormModalControlled as FormModalControlledCommon,
  type FormModalControlledProps as FormModalControlledPropsCommon,
  type FormModalControlledRenderParams
} from "@common/controls/Modal/Modals/Form/component";
import classnames from "classnames";
import { identity, isFunction } from "lodash";
import * as React from "react";

export { type FormModalControlledRenderParams };

export type FormModalButtonProps = {
  isBusy: boolean;
  onClick: (e: React.MouseEvent) => void | Promise<void>;
};
export type FormModalButtonDefinition = {
  title: string;
  type: "cancel" | "submit";
};
export type FormModalButton = FormModalButtonDefinition | ((props: FormModalButtonProps) => any);

export type FormModalProps = Pick<ModalProps, "closeBehaviour"> & {
  buttons: FormModalButton[];
  buttonsStyle?: React.CSSProperties;
  bottomInfo?: string | React.ReactElement | null;
  topInfo?: string | React.ReactElement | null;
  className?: string;
  hasBackdrop?: boolean;
  isAdminColor?: boolean;
  isInViewport?: boolean;
  isSubmitting?: boolean;
  isVisible: boolean;
  title: string;
  error?: string | null;
  footerBottomInfo?: string | React.ReactElement | null;

  fields: FormFieldInfo[];
  additionalFields?: FormFieldInfo[] | DynamicForms.Field[];
  additionalFieldsPlacement?: "aboveButton" | "belowButton";
  width?: string | number;

  onClose?: () => void;
  onChange?: (name: string, value: any) => void;
  onSubmit: (data: object) => Promise<any> | void;

  uploadApiInfo?: UploadEndpointInfo | null;

  formProps?: any;
  Form?: React.ComponentType<any>;
  buttonsPosition?: "left" | "right" | "stretch";
};

const FormModal = (props: FormModalProps) => {
  const formRef = React.useRef<FormHandles>(null);

  const onSubmit = React.useCallback(
    (e: React.MouseEvent) => formRef.current!.ifValidated((data) => props.onSubmit(data))(e),
    [props.onSubmit, formRef]
  );

  const FormComponent = props.Form ?? Form;
  return (
    <Modal
      className={classnames("wsc_form_modal", props.className)}
      closeBehaviour={props.isSubmitting ? "not-allowed" : props.closeBehaviour}
      hasBackdrop={props.hasBackdrop}
      isAdminColor={props.isAdminColor ?? false}
      isCentered
      isInViewport={props.isInViewport ?? true}
      isVisible={props.isVisible}
      onClose={props.onClose}
      width={props.width}
    >
      <Modal.Header title={props.title} />
      <Modal.Body>
        <FormComponent
          ref={formRef}
          {...props.formProps}
          fields={props.fields}
          onChange={props.onChange}
          uploadApiInfo={props.uploadApiInfo}
          additionalFields={props.additionalFields}
          additionalFieldsPlacement={props.additionalFieldsPlacement}
          bottomInfo={props.bottomInfo}
          topInfo={props.topInfo}
        />

        {props.error && <div className="error_box">{props.error}</div>}
      </Modal.Body>
      <Modal.Footer contentPosition={props.buttonsPosition}>
        <div className="buttons" style={props.buttonsStyle}>
          {props.buttons.map((btn, i) => {
            if (isFunction(btn)) {
              const Cmp = btn;
              return <Cmp isBusy={props.isSubmitting ?? false} onClick={onSubmit} />;
            }

            const b = btn as FormModalButtonDefinition;
            return (
              <AsyncButton
                key={i}
                className={`btn-${b.type === "cancel" ? "light" : "primary"}`}
                action={b.type === "cancel" ? props.onClose || identity : onSubmit}
                isBusy={b.type === "submit" && props.isSubmitting}
              >
                {b.title}
              </AsyncButton>
            );
          })}
        </div>
        {typeof props.footerBottomInfo === "string" ? (
          <div className="info" dangerouslySetInnerHTML={{ __html: props.footerBottomInfo }} />
        ) : (
          props.footerBottomInfo
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default FormModal;

export type FormModalControlledProps<T> = Omit<FormModalControlledPropsCommon<T>, "containerSelector"> & {
  isInViewport?: boolean;
};
export const FormModalControlled = <T extends object>(props: FormModalControlledProps<T>) => (
  <FormModalControlledCommon {...props} containerSelector={props.isInViewport ? ".wsc_viewport" : undefined} />
);
